import {
  AnyAction,
  combineReducers,
  configureStore,
  DevToolsEnhancerOptions,
} from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';

import { ENVIRONMENT, SliceName } from '@/shared/config';

// Legacy for client
import authReducer from '@/entities/auth/model/authSlice';
import billingReducer from '@/entities/billing/model/billingSlice';
import organizationReducer from '@/entities/organization/model/organizationSlice';
import organizationsReducer from '@/entities/organizations/model/organizationsSlice';
import productEditorReducer from '@/entities/productEditor/model/productEditorSlice';
import userReducer from '@/entities/user/model/userSlice';
import usersReducer from '@/entities/users/model/usersSlice';

import { errorMiddleware } from './errorMiddleware';

const appReducer = combineReducers({
  [SliceName.auth]: authReducer,
  [SliceName.user]: userReducer,
  [SliceName.billing]: billingReducer,
  [SliceName.users]: usersReducer,
  [SliceName.organization]: organizationReducer,
  [SliceName.organizations]: organizationsReducer,
  [SliceName.productEditor]: productEditorReducer,
});

const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: AnyAction,
) => {
  if (action.type === 'store/reset') {
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

const devToolsConfig: DevToolsEnhancerOptions = {
  maxAge: 10,
};

export const setupStore = () =>
  configureStore({
    devTools: ENVIRONMENT !== 'production' && devToolsConfig,
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }).concat(errorMiddleware),
  });

export const store = setupStore();

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
